import { useEffect, useMemo, useRef } from 'react'
import { useCompanyAccessStore, type CompanyAccessState } from '../stores/companyAccessStore'
import { usePermissionStore } from '../stores/permissionStore'
import { CompanyService } from '../services/CompanyService'
import { accountStatusService } from '../services/AccountStatusService'
import {
  resolveCompanyAccessGate,
  type CompanyAccessGate,
} from '../components/MainMenu/utils/suspendedCompanyGate'
import { isCompanyAccessReloadHeld, onCompanyAccessReloadReleased } from './companyAccessReloadHold'

/** A suspension shows up within this long even if the tab never loses focus. */
export const COMPANY_ACCESS_POLL_MS = 5 * 60 * 1000

/** Alt-tabbing fires focus repeatedly; one check per few seconds is "at once" for a person. */
export const COMPANY_ACCESS_FOCUS_MIN_AGE_MS = 5_000

/**
 * The suspended-company gate for the signed-in user (SC-409). `isElevatedAdmin` is the
 * existing platform-admin bypass; admins are never gated.
 */
export function useCompanyAccessGate(
  currentUserId: string | null,
  isElevatedAdmin: boolean
): CompanyAccessGate {
  const access = useCompanyAccessStore((s) => s.access)
  const status = useCompanyAccessStore((s) => s.status)
  const accessUserId = useCompanyAccessStore((s) => s.userId)
  const adminCapabilitiesVerified = usePermissionStore((s) => s.adminCapabilitiesVerified)

  return useMemo(
    () =>
      resolveCompanyAccessGate({
        access,
        status,
        accessUserId,
        currentUserId,
        adminCapabilitiesVerified,
        isElevatedAdmin,
      }),
    [access, status, accessUserId, currentUserId, adminCapabilitiesVerified, isElevatedAdmin]
  )
}

function isColyseusRoomActive(): boolean {
  try {
    return !!(window as unknown as { networkInstance?: { room?: unknown } }).networkInstance?.room
  } catch {
    return false
  }
}

/**
 * Reactivation restores the normal UI with a full page load, after dropping the caches that
 * would otherwise serve the "disabled" company again. In-place refreshing is not enough: the
 * disabled view is cached independently by every `useCompanyPermissions` instance, by
 * `useAccountStatus` and by the earlier refused lobby
 * connection. The `companyAccessRevoked` flow uses the same full-load approach.
 *
 * The load goes to `/`, except when the URL still holds a meeting (`?room=`) or room-invite
 * (`?rid=`) link: the gate held that link back without saving it anywhere, so reloading the
 * same path and query is the only way the member still lands on it. Only the path and query
 * of the current page are reused, so the target is always this origin.
 *
 * An object so tests can replace `restore` without navigating jsdom.
 */
export const companyAccessReactivation = {
  restore(): void {
    CompanyService.clearCache()
    accountStatusService.clearCache()
    ;(window as any).bypassBeforeUnload = true
    const { origin, pathname, search } = window.location
    const params = new URLSearchParams(search || '')
    const holdsDeepLink = !!(params.get('room') || params.get('rid'))
    window.location.href = holdsDeepLink ? `${origin}${pathname}${search}` : `${origin}/`
  },
}

/**
 * Keeps the access store current while the workspace menu is mounted for `userId`: on sign-in
 * (mount or user change), on window focus and every five minutes. StrictMode's double mount
 * issues one request, because `refresh` is single-flight.
 *
 * A reactivation found while a profile or feedback edit is open waits for it to close
 * (`useHoldCompanyAccessReload`), so the reload never discards unsaved input.
 */
export function useCompanyAccessRefresh(userId: string | null, isElevatedAdmin: boolean): void {
  const elevatedRef = useRef(isElevatedAdmin)
  elevatedRef.current = isElevatedAdmin

  useEffect(() => {
    if (!userId) return
    const refresh = (maxAgeMs?: number) =>
      void useCompanyAccessStore.getState().refresh(maxAgeMs === undefined ? {} : { maxAgeMs })

    refresh()
    const onFocus = () => refresh(COMPANY_ACCESS_FOCUS_MIN_AGE_MS)
    window.addEventListener('focus', onFocus)
    // A hidden tab has nobody to show a change to, and coming back fires the focus refresh.
    const interval = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return
      refresh()
    }, COMPANY_ACCESS_POLL_MS)

    return () => {
      window.removeEventListener('focus', onFocus)
      window.clearInterval(interval)
    }
  }, [userId])

  useEffect(() => {
    if (!userId) return
    const selectBlocked = (s: CompanyAccessState) =>
      s.userId === userId ? s.access?.blocked ?? null : null
    // A reactivation seen while a profile or feedback edit is open, waiting for it to close.
    let deferred = false

    const restoreUnlessExempt = () => {
      deferred = false
      // Admins were never gated, and a reload would throw a player out of their room.
      if (elevatedRef.current || isColyseusRoomActive()) return
      if (isCompanyAccessReloadHeld()) {
        deferred = true
        return
      }
      companyAccessReactivation.restore()
    }

    const unsubscribe = useCompanyAccessStore.subscribe(
      selectBlocked,
      (blocked, previouslyBlocked) => {
        // Suspended again (or no longer known) before the edit closed: the gate stays, no reload.
        if (blocked !== false) deferred = false
        if (previouslyBlocked !== true || blocked !== false) return
        restoreUnlessExempt()
      }
    )
    const stopListening = onCompanyAccessReloadReleased(() => {
      if (deferred && selectBlocked(useCompanyAccessStore.getState()) === false) {
        restoreUnlessExempt()
      }
    })
    return () => {
      unsubscribe()
      stopListening()
    }
  }, [userId])
}
